import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaExclamationTriangle, FaTimes } from "react-icons/fa";
import { apiFetch } from "./api";
import PracticeModal from "./PracticeModal";
import PracticeNoteModal from "./PracticeNoteModal";
import PracticeCsvImport from "./PracticeCsvImport";
import { formatPracticeTimeRange } from "./utils";
function formatPracticePrimaryLabel(practice) {
  const typeValue = normalizePracticeType(practice?.type);
  if (typeValue === "regatta") {
    return (practice?.name || "").trim() || "Regatta";
  }
  if (typeValue === "event") {
    const name = (practice?.name || "").trim() || "Event";
    const timeLabel = formatPracticeTimeRange(practice);
    if (timeLabel && timeLabel !== "All day") {
      return `${name} • ${timeLabel}`;
    }
    return name;
  }
  return formatPracticeTimeRange(practice);
}
function formatPracticeTypeLabel(typeValue) {
  const value = String(typeValue || "").trim().toLowerCase();
  if (value === "event") return "Event";
  if (value === "regatta") return "Regatta";
  return "Practice";
}
function normalizePracticeType(typeValue) {
  const value = String(typeValue || "").trim().toLowerCase();
  if (["event", "practice", "regatta"].includes(value)) return value;
  return "practice";
}

function normalizeRsvpStatus(status) {
  const value = typeof status === "string" ? status.toLowerCase() : "";
  if (["available", "present", "yes", "attending", "in"].includes(value)) return "available";
  if (["not available", "unavailable", "absent", "no", "out", "late", "excused"].includes(value)) return "unavailable";
  return "none";
}

const RSVP_STYLES = {
  available: { backgroundColor: "rgba(52,211,153,0.15)", borderColor: "rgba(52,211,153,0.4)", color: "var(--success)" },
  unavailable: { backgroundColor: "rgba(248,113,113,0.15)", borderColor: "rgba(248,113,113,0.4)", color: "var(--danger)" },
  none: { backgroundColor: "var(--bg-elevated)", borderColor: "var(--border)", color: "var(--muted)" },
};

const RSVP_ICONS = {
  available: "✓",
  unavailable: "✗",
  none: "?",
};

function fmt(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getDateKey(value) {
  if (!value) return null;
  if (typeof value === "string") return value.slice(0, 10);
  return fmt(new Date(value));
}

export default function PracticeCalendar({ squads, coach, userId = null, teamColors = {}, canEditPractices = false, onPracticeClick = null, fetchPractices = null, focusRequest = null }) {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [isPhoneLayout, setIsPhoneLayout] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(max-width: 600px)").matches;
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [practices, setPractices] = useState([]);
  const [teams, setTeams] = useState([]);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    date: "",
    endDate: "",
    startTime: "06:00",
    endTime: "",
    state: "on",
    type: "practice",
    squadIds: [],
    team: "",
    note: "",
  });

  const [notePracticeId, setNotePracticeId] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [cancelNote, setCancelNote] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const [creating, setCreating] = useState(null);
  const [createForm, setCreateForm] = useState({
    date: "",
    endDate: "",
    name: "",
    location: "",
    startTime: "06:00",
    endTime: "",
    state: "on",
    type: "practice",
    squadIds: [],
    team: "",
    note: "",
  });

  const [practiceDetails, setPracticeDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [updatingCoachAttendance, setUpdatingCoachAttendance] = useState(false);
  const lastProcessedFocusRef = useRef(null);
  const allowCalendarEdits = Boolean(canEditPractices);
  const isPracticeClickable = allowCalendarEdits || Boolean(onPracticeClick);
  const viewerId = coach?.id ? Number(coach.id) : userId ? Number(userId) : null;
  const viewerType = coach?.id ? "coach" : "athlete";
  const handlePracticeButtonClick = (event, practice) => {
    if (!allowCalendarEdits && !onPracticeClick) return;
    if (allowCalendarEdits) {
      event?.stopPropagation();
      openEdit(practice);
      return;
    }
    if (onPracticeClick) {
      event?.stopPropagation();
      onPracticeClick(practice);
    }
  };

  const parseLocalDate = (value) => {
    if (!value) return null;
    const parts = String(value).split("-");
    if (parts.length !== 3) return null;
    const [yearStr, monthStr, dayStr] = parts;
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if ([year, month, day].some((n) => Number.isNaN(n))) return null;
    return new Date(year, month - 1, day);
  };
  const dayDiff = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 0;
    const ms = toDate.getTime() - fromDate.getTime();
    return Math.floor(ms / (24 * 60 * 60 * 1000));
  };
  const spansIntoNextDay = (startKey, endKey) => {
    if (!startKey || !endKey) return false;
    const startDate = parseLocalDate(startKey);
    const endDate = parseLocalDate(endKey);
    if (!startDate || !endDate) return false;
    const next = new Date(startDate);
    next.setDate(next.getDate() + 1);
    return endDate >= next;
  };
  const todayKey = fmt(new Date());
  const monthGridGap = 12;
  const spanRowHeight = 44;
  const spanRowGap = 8;

  const startOfWeek = useCallback((d) => {
    const x = new Date(d);
    const dow = x.getDay();
    x.setDate(x.getDate() - dow);
    x.setHours(0, 0, 0, 0);
    return x;
  }, []);

  const endOfWeek = useCallback((d) => {
    const x = startOfWeek(d);
    x.setDate(x.getDate() + 6);
    return x;
  }, [startOfWeek]);

  const squadMap = useMemo(() => {
    const map = new Map();
    (squads || []).forEach((sq) => {
      if (sq?.id) map.set(Number(sq.id), sq.name);
    });
    return map;
  }, [squads]);

  const normalizePractices = useCallback(
    (items) =>
      (Array.isArray(items) ? items : []).map((p) => {
        const squadId = p.squadId ?? p.squad_id ?? null;
        const squadIds = Array.isArray(p.squadIds) ? p.squadIds : (squadId ? [squadId] : []);
        const squadName = squadIds.length > 0
          ? squadIds.map((sid) => squadMap.get(Number(sid))).filter(Boolean).join(", ") || p.squadName || p.squad || null
          : (p.squadName || p.squad || null);
        return {
          ...p,
          squadId: squadId || null,
          squadIds,
          squadName,
          squad: squadName,
        };
      }),
    [squadMap]
  );

  const y = cursor.getFullYear();
  const m = cursor.getMonth();

  const { rangeStart, rangeEnd } = useMemo(() => {
    if (view === "month") {
      const first = new Date(y, m, 1);
      const last = new Date(y, m + 1, 0);
      return {
        rangeStart: startOfWeek(first),
        rangeEnd: endOfWeek(last),
      };
    }
    const base = selectedDate ? parseLocalDate(selectedDate) || new Date() : new Date();
    return {
      rangeStart: startOfWeek(base),
      rangeEnd: endOfWeek(base),
    };
  }, [view, y, m, selectedDate, startOfWeek, endOfWeek]);

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      const query = window.matchMedia("(max-width: 600px)");
      const handleChange = () => setIsPhoneLayout(query.matches);
      handleChange();
      if (typeof query.addEventListener === "function") {
        query.addEventListener("change", handleChange);
      } else if (typeof query.addListener === "function") {
        query.addListener(handleChange);
      }
      return () => {
        if (typeof query.removeEventListener === "function") {
          query.removeEventListener("change", handleChange);
        } else if (typeof query.removeListener === "function") {
          query.removeListener(handleChange);
        }
      };
    }
    return undefined;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const teamsData = await apiFetch('/teams');
        setTeams(teamsData);
      } catch (e) {
        console.error("Failed to load teams:", e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");
        const from = fmt(rangeStart);
        const to = fmt(rangeEnd);

        const practicePromise = fetchPractices ? fetchPractices(from, to) : apiFetch(`/practices/range?from=${from}&to=${to}`);
        const attendancePromise = viewerId
          ? apiFetch(
              viewerType === "coach"
                ? `/attendance/coach/range?user=${viewerId}&from=${from}&to=${to}`
                : `/attendance/range?user=${viewerId}&from=${from}&to=${to}`
            )
          : Promise.resolve([]);

        const [practiceRows, attendanceRows] = await Promise.all([practicePromise, attendancePromise]);

        const attendanceMap = new Map(
          (Array.isArray(attendanceRows) ? attendanceRows : []).map((row) => [row.practice_id, row.status || null])
        );

        const normalized = normalizePractices(practiceRows).map((p) => ({
          ...p,
          rsvpStatus: attendanceMap.get(p.id) || null,
        }));

        setPractices(normalized);
      } catch (e) {
        setError(e?.message || "Failed to load practices");
      } finally {
        setLoading(false);
      }
    })();
  }, [rangeStart, rangeEnd, normalizePractices, viewerId, viewerType, fetchPractices]);

  const byDay = useMemo(() => {
    const map = new Map();
    practices.forEach((p) => {
      const key = typeof p.date === "string" ? p.date.slice(0, 10) : fmt(new Date(p.date));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.startTime || "99:99").localeCompare(b.startTime || "99:99"));
    }
    return map;
  }, [practices]);
  const practiceStartMap = useMemo(() => {
    const map = new Map();
    practices.forEach((p) => {
      if (!p?.id) return;
      const key = getDateKey(p.startDate || p.date);
      if (!key) return;
      const prev = map.get(p.id);
      if (!prev || key < prev) map.set(p.id, key);
    });
    return map;
  }, [practices]);

  function daysArray() {
    const out = [];
    const d = new Date(rangeStart);
    while (d <= rangeEnd) {
      out.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return out;
  }
  const days = daysArray();
  const todayStart = (() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  })();
  const showUpcomingOnly = isPhoneLayout && view === "month" && todayStart >= rangeStart && todayStart <= rangeEnd;
  const monthDays = showUpcomingOnly ? days.filter((dateObj) => dateObj >= todayStart) : days;

  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const weekLabel = `${rangeStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${rangeEnd.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  const gotoPrev = () => {
    if (view === "month") setCursor(new Date(y, m - 1, 1));
    else {
      const base = new Date(rangeStart);
      base.setDate(base.getDate() - 7);
      setSelectedDate(fmt(base));
    }
  };
  const gotoNext = () => {
    if (view === "month") setCursor(new Date(y, m + 1, 1));
    else {
      const base = new Date(rangeStart);
      base.setDate(base.getDate() + 7);
      setSelectedDate(fmt(base));
    }
  };

  async function updateOwnAttendance(practiceId, status) {
    if (!coach?.id || !practiceId) return;
    try {
      setUpdatingCoachAttendance(true);
      await apiFetch(`/attendance/coach/mine?as=${coach.id}`, {
        method: "POST",
        body: JSON.stringify({
          status: status,
          practice_id: practiceId,
        }),
      });
      setPractices((prev) =>
        prev.map((p) => (p.id === practiceId ? { ...p, rsvpStatus: status } : p))
      );
      await loadPracticeDetails(practiceId);
    } catch (e) {
      alert(e?.message || "Failed to update attendance");
    } finally {
      setUpdatingCoachAttendance(false);
    }
  }

  async function loadPracticeDetails(practiceId) {
    if (!practiceId) return;
    try {
      setLoadingDetails(true);

      const practiceData = await apiFetch(`/practices/${practiceId}`);
      const practiceDate = practiceData.date?.slice(0, 10);

      let coachesWithStatus = [];
      if (practiceDate) {
        try {
          const coachAttendance = await apiFetch(
            `/attendance/coach/by-date?date=${practiceDate}&practice=${practiceId}`
          );
          coachesWithStatus = (coachAttendance || []).map((coachData) => ({
            name: coachData.coachName,
            userId: coachData.id,
            status: coachData.status || null,
          }));
        } catch (e) {
          console.warn("Could not fetch coach attendance:", e);
        }
      }

      let attendanceSummary = null;
      try {
        const summary = await apiFetch(`/attendance/summary?practiceId=${practiceId}`);
        if (Array.isArray(summary) && summary.length) {
          attendanceSummary = summary;
        }
      } catch (e) {
        console.warn("Could not fetch attendance summary:", e);
      }

      if (!attendanceSummary) {
        try {
          const rows = await apiFetch(`/attendance/by-practice/${practiceId}`);
          const teamMap = new Map();

          (Array.isArray(rows) ? rows : []).forEach((row) => {
            const teamName = row.team || row.teamName || "Unassigned";
            const status = (row.status || "unknown").toLowerCase();

            if (!teamMap.has(teamName)) {
              teamMap.set(teamName, { team: teamName, available: 0, unavailable: 0, unknown: 0 });
            }

            const teamData = teamMap.get(teamName);
            if (["present", "yes", "attending", "in", "available"].includes(status)) {
              teamData.available += 1;
            } else if (["absent", "no", "out", "unavailable", "late", "excused"].includes(status)) {
              teamData.unavailable += 1;
            } else {
              teamData.unknown += 1;
            }
          });

          attendanceSummary = Array.from(teamMap.values()).sort((a, b) => a.team.localeCompare(b.team));
        } catch (e) {
          console.warn("Could not fetch detailed attendance:", e);
        }
      }

      let workouts = [];
      try {
        const workoutList = await apiFetch(`/practices/${practiceId}/workouts`);
        workouts = Array.isArray(workoutList) ? workoutList : [];
      } catch (e) {
        console.warn("Could not fetch workouts:", e);
      }

      let lineupStatuses = [];
      try {
        const boatsData = await apiFetch(`/boats/by-practice/${practiceId}`);
        const boats = Array.isArray(boatsData?.boats) ? boatsData.boats : [];
        const teamSet = [...new Set(boats.map((b) => b.team).filter(Boolean))];

        let publishedByTeam = {};
        if (practiceDate && teamSet.length > 0) {
          try {
            const pubData = await apiFetch(`/lineups/${practiceDate}/published-squads`);
            (pubData?.items || []).forEach((item) => {
              if (!publishedByTeam[item.team]) publishedByTeam[item.team] = [];
              publishedByTeam[item.team].push(item);
            });
          } catch (e) {
            console.warn("Could not fetch published squads:", e);
          }
        }

        lineupStatuses = teamSet.map((team) => {
          const pubEntries = publishedByTeam[team] || [];
          const isPublished = pubEntries.length > 0;
          return {
            team,
            published: isPublished,
            publishedAt: isPublished ? pubEntries[0].publishedAt : null,
            publishedBy: isPublished ? pubEntries[0].publishedBy : null,
          };
        });
      } catch (e) {
        console.warn("Could not fetch lineup statuses:", e);
      }

      setPracticeDetails({
        practiceInfo: practiceData,
        coaches: coachesWithStatus,
        attendance: attendanceSummary,
        workouts,
        lineupStatuses,
      });
    } catch (e) {
      console.error("Failed to load practice details:", e);
      setPracticeDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  }

  function openEdit(p) {
    if (!allowCalendarEdits) return;
    const startDate = p.startDate || p.date || "";
    setEditing(p);
    setForm({
      name: p.name || "",
      abbrevName: p.abbrevName || "",
      location: p.location || "",
      date: startDate,
      endDate: p.endDate || "",
      startTime: (p.startTime || p.time || "06:00").slice(0, 5),
      endTime: (p.endTime || "").slice(0, 5),
      state: p.state || "on",
      type: normalizePracticeType(p.type),
      squadIds: Array.isArray(p.squadIds) ? p.squadIds.map(String) : (p.squadId ? [String(p.squadId)] : []),
      team: p.team || "",
      note: p.note || "",
      responseFields: Array.isArray(p.responseFields)
        ? p.responseFields.map((field) => ({
            id: field.id,
            label: field.label || "",
            type: field.type || "text",
            options: Array.isArray(field.options) ? field.options : [],
          }))
        : [],
    });
    setPracticeDetails(null);
    loadPracticeDetails(p.id);
  }

  // Opens the same editable practice modal when navigated here from elsewhere
  // (e.g. clicking "Details" on the home page's next-practice tile).
  useEffect(() => {
    if (!allowCalendarEdits) return;
    if (!focusRequest?.practiceId || !focusRequest?.ts) return;
    if (lastProcessedFocusRef.current === focusRequest.ts) return;
    lastProcessedFocusRef.current = focusRequest.ts;
    const existing = practices.find((p) => p.id === focusRequest.practiceId);
    openEdit(existing || { id: focusRequest.practiceId, date: focusRequest.date || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest, allowCalendarEdits, practices]);

  function openCreatePractice(forDate) {
    if (!allowCalendarEdits) return;
    setCreating(forDate);
    setCreateForm({
      date: forDate,
      endDate: "",
      name: "",
      abbrevName: "",
      location: "",
      startTime: "06:00",
      endTime: "",
      state: "on",
      type: "practice",
      squadIds: [],
      team: "",
      note: "",
      responseFields: [],
    });
  }

  async function createPractice() {
    if (!allowCalendarEdits || !creating) return;
    try {
      await apiFetch(`/practices`, {
        method: "POST",
        body: JSON.stringify({
          date: createForm.date || creating,
          endDate: createForm.endDate || null,
          name: createForm.name || "",
          abbrevName: createForm.abbrevName || "",
          location: createForm.location || "",
          startTime: createForm.startTime || null,
          endTime: createForm.endTime || null,
          state: createForm.state,
          type: normalizePracticeType(createForm.type),
          squadIds: (createForm.squadIds || []).map(Number).filter(Boolean),
          team: createForm.team || null,
          note: createForm.note || "",
          responseFields: Array.isArray(createForm.responseFields) ? createForm.responseFields : [],
        }),
      });
      setCreating(null);
      const data = await (fetchPractices ? fetchPractices(fmt(rangeStart), fmt(rangeEnd)) : apiFetch(`/practices/range?from=${fmt(rangeStart)}&to=${fmt(rangeEnd)}`));
      setPractices(normalizePractices(data));
    } catch (e) {
      alert(e.message || "Failed to create practice");
    }
  }

  async function saveEdit() {
    if (!allowCalendarEdits || !editing) return;
    try {
      await apiFetch(`/practices/${editing.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: form.name || "",
          abbrevName: form.abbrevName || "",
          location: form.location || "",
          date: form.date || editing.startDate || editing.date || "",
          endDate: form.endDate || null,
          startTime: form.startTime || null,
          endTime: form.endTime || null,
          state: form.state,
          type: normalizePracticeType(form.type),
          squadIds: (form.squadIds || []).map(Number).filter(Boolean),
          team: form.team || null,
          note: form.note || "",
          responseFields: Array.isArray(form.responseFields) ? form.responseFields : [],
        }),
      });
      setEditing(null);
      const data = await (fetchPractices ? fetchPractices(fmt(rangeStart), fmt(rangeEnd)) : apiFetch(`/practices/range?from=${fmt(rangeStart)}&to=${fmt(rangeEnd)}`));
      setPractices(normalizePractices(data));
    } catch (e) {
      alert(e.message || "Failed to save");
    }
  }

  async function deletePractice() {
    if (!allowCalendarEdits || !editing) return;
    if (!window.confirm("Delete this practice?")) return;
    try {
      await apiFetch(`/practices/${editing.id}`, { method: "DELETE" });
      setEditing(null);
      const data = await (fetchPractices ? fetchPractices(fmt(rangeStart), fmt(rangeEnd)) : apiFetch(`/practices/range?from=${fmt(rangeStart)}&to=${fmt(rangeEnd)}`));
      setPractices(normalizePractices(data));
    } catch (e) {
      alert(e.message || "Failed to delete");
    }
  }

  async function submitCancelPractice() {
    if (!allowCalendarEdits || !cancelling) return;
    const note = cancelNote.trim();
    if (!note) return;
    setCancelSubmitting(true);
    try {
      await apiFetch(`/practices/${cancelling}/cancel`, {
        method: "PATCH",
        body: JSON.stringify({ note }),
      });
      setCancelling(null);
      setCancelNote("");
      setEditing(null);
      const data = await (fetchPractices ? fetchPractices(fmt(rangeStart), fmt(rangeEnd)) : apiFetch(`/practices/range?from=${fmt(rangeStart)}&to=${fmt(rangeEnd)}`));
      setPractices(normalizePractices(data));
    } catch (e) {
      alert(e.message || "Failed to cancel practice");
    } finally {
      setCancelSubmitting(false);
    }
  }

  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="row" style={{ alignItems: "center", gap: 8 }}>
        <div className={`calendar-view-toggle ${isPhoneLayout ? "compact" : ""}`}>
          <button
            className={view === "month" ? "active" : ""}
            onClick={() => setView("month")}
          >
            {isPhoneLayout ? "M" : "Month"}
          </button>
          <button
            className={view === "week" ? "active" : ""}
            onClick={() => setView("week")}
          >
            {isPhoneLayout ? "W" : "Week"}
          </button>
        </div>
        <button className="btn ghost" onClick={gotoPrev}>
          &larr;
        </button>
        <h2 className="h2" style={{ margin: "0 12px", flex: 1, textAlign: "center" }}>
          {view === "month" ? monthLabel : weekLabel}
        </h2>
        <button className="btn ghost" onClick={gotoNext}>
          &rarr;
        </button>
      </div>

      {allowCalendarEdits && (
        <div style={{ marginTop: 8 }}>
          <PracticeCsvImport onImported={() => {
            const start = new Date(y, m, 1);
            const end = new Date(y, m + 1, 0);
            (fetchPractices ? fetchPractices(fmt(start), fmt(end)) : apiFetch(`/practices/range?from=${fmt(start)}&to=${fmt(end)}`)).then((data) => {
              setPractices(normalizePractices(data));
            }).catch(() => {});
          }} />
        </div>
      )}

      {loading && <p className="help" style={{ marginTop: 8 }}>Loading…</p>}
      {error && (
        <p className="help" style={{ marginTop: 8, color: "red" }}>
          {error}
        </p>
      )}

      {view === "month" ? (
        <>
        <div className="weekdayHeader" style={{ marginTop: 6 }}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} style={{ textAlign: "center", fontWeight: 600 }}>{d}</div>
          ))}
        </div>
        <div className="calendarMonthGrid" style={{ marginTop: 4 }}>
          {monthDays.map((dateObj, idx) => {
            const key = fmt(dateObj);
            const inMonth = dateObj.getMonth() === m;
            const isToday = key === todayKey;
            const dayPractices = byDay.get(key) || [];
            const isSelected = selectedDate === key;
            const columnIndex = idx % 7;
            const shortWeekday = dateObj.toLocaleDateString(undefined, { weekday: "short" });
            const shortDate = dateObj.toLocaleDateString(undefined, { month: "short", day: "numeric" });
            const formattedDateLabel = isPhoneLayout ? `${shortWeekday} • ${shortDate}` : shortDate;
            const visibleDayPractices = dayPractices.filter((p) => {
              const startKey = getDateKey(p.startDate || practiceStartMap.get(p.id) || p.date);
              const endKey = getDateKey(p.endDate);
              if (!spansIntoNextDay(startKey, endKey)) return true;
              if (key === startKey) return true;
              const startDate = parseLocalDate(startKey);
              const endDate = parseLocalDate(endKey);
              const currentDate = parseLocalDate(key);
              if (!startDate || !endDate || !currentDate) return false;
              if (isPhoneLayout) return currentDate > startDate && currentDate <= endDate;
              if (columnIndex !== 0) return false;
              return currentDate > startDate && currentDate <= endDate;
            }).sort((a, b) => {
              if (view !== "month" || isPhoneLayout) return 0;
              const aStartKey = getDateKey(a.startDate || practiceStartMap.get(a.id) || a.date);
              const bStartKey = getDateKey(b.startDate || practiceStartMap.get(b.id) || b.date);
              const aEndKey = getDateKey(a.endDate);
              const bEndKey = getDateKey(b.endDate);
              const aIsMulti = spansIntoNextDay(aStartKey, aEndKey);
              const bIsMulti = spansIntoNextDay(bStartKey, bEndKey);
              if (aIsMulti !== bIsMulti) return aIsMulti ? -1 : 1;
              return (a.startTime || "99:99").localeCompare(b.startTime || "99:99");
            });
            const incomingSpanCount = (() => {
              if (view !== "month" || isPhoneLayout) return 0;
              const currentDate = parseLocalDate(key);
              if (!currentDate) return 0;
              const weekStart = startOfWeek(currentDate);
              return dayPractices.reduce((count, p) => {
                const startKey = getDateKey(p.startDate || practiceStartMap.get(p.id) || p.date);
                const endKey = getDateKey(p.endDate);
                if (!spansIntoNextDay(startKey, endKey)) return count;
                const startDate = parseLocalDate(startKey);
                const endDate = parseLocalDate(endKey);
                if (!startDate || !endDate) return count;
                const barOrigin = startDate < weekStart ? weekStart : startDate;
                if (currentDate > barOrigin && currentDate <= endDate) return count + 1;
                return count;
              }, 0);
            })();
            const listOffset =
              incomingSpanCount > 0
                ? incomingSpanCount * spanRowHeight + (incomingSpanCount - 1) * spanRowGap
                : 0;
            const hasSpan = visibleDayPractices.some((p) => {
              const startKey = getDateKey(p.startDate || practiceStartMap.get(p.id) || p.date);
              const endKey = getDateKey(p.endDate);
              if (!spansIntoNextDay(startKey, endKey)) return false;
              const startDate = parseLocalDate(startKey);
              const endDate = parseLocalDate(endKey);
              const currentDate = parseLocalDate(key);
              if (!startDate || !endDate || !currentDate) return false;
              const weekEnd = endOfWeek(currentDate);
              const spanEnd = endDate < weekEnd ? endDate : weekEnd;
              const spanDays = dayDiff(currentDate, spanEnd) + 1;
              return (
                view === "month" &&
                !isPhoneLayout &&
                spanDays > 1
              );
            });

            return (
              <div
                key={idx}
                className={`card calendarDayCard ${inMonth ? "" : "outside"} ${isToday ? "today" : ""} ${isSelected ? "selected" : ""} ${hasSpan ? "has-span" : ""}`}
                style={{
                  cursor: allowCalendarEdits ? "pointer" : "default",
                  opacity: inMonth ? 1 : 0.45,
                }}
                onClick={() => setSelectedDate(key)}
              >
                <div className="calendarDayHeader">
                  <div>
                    <div className="calendarDayWeekday">
                      {shortWeekday}
                    </div>
                    <div className={`calendarDayNumber ${isToday ? "today" : ""}`}>
                      {formattedDateLabel}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {isToday && <span className="todayPill">Today</span>}
                    {allowCalendarEdits && (
                      <button
                        className="btn ghost tiny"
                        style={{ padding: "4px 8px", minHeight: "auto" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openCreatePractice(key);
                        }}
                        title="Add practice"
                        type="button"
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>

                <div
                  className="calendarPracticeList"
                  style={listOffset ? { paddingTop: listOffset } : undefined}
                >
                  {visibleDayPractices.length === 0 && (
                    <span className="help" style={{ opacity: 0.6 }}>No practices</span>
                  )}
                  {visibleDayPractices.map((p) => {
                    const startKey = getDateKey(p.startDate || practiceStartMap.get(p.id) || p.date);
                    const endKey = getDateKey(p.endDate);
                    const endDate = endKey ? parseLocalDate(endKey) : null;
                    const currentDate = parseLocalDate(key);
                    const weekEnd = currentDate ? endOfWeek(currentDate) : null;
                    const spanEnd = endDate && weekEnd && endDate < weekEnd ? endDate : weekEnd;
                    const spanDays =
                      currentDate && spanEnd ? Math.max(1, dayDiff(currentDate, spanEnd) + 1) : 1;
                    const shouldSpan =
                      view === "month" &&
                      !isPhoneLayout &&
                      spansIntoNextDay(startKey, endKey) &&
                      spanDays > 1;
                    const labelTime = formatPracticePrimaryLabel(p);
                    const teamName = p.team ? String(p.team).trim() : "";
                    const stateClass = p.cancelled ? "cancelled" : (p.state || "on").toLowerCase();
                    const statusKey = normalizeRsvpStatus(p.rsvpStatus);
                    const rsvpClass =
                      statusKey === "available" ? "rsvp-present" : statusKey === "unavailable" ? "rsvp-absent" : "rsvp-none";
                    const rsvpStyle = RSVP_STYLES[statusKey] || RSVP_STYLES.none;
                    const icon = RSVP_ICONS[statusKey] || "?";
                    const typeLabel = formatPracticeTypeLabel(p.type);

                    return (
                      <button
                        key={`${p.id}-${p.date}`}
                        type="button"
                        className={`calendarPracticeButton state-${stateClass} ${rsvpClass} ${shouldSpan ? "multi-day-span" : ""}`}
                        style={{
                          width: shouldSpan ? `calc(${spanDays * 100}% + ${(spanDays - 1) * monthGridGap}px)` : "100%",
                          cursor: isPracticeClickable ? "pointer" : "default",
                          opacity: isPracticeClickable ? 1 : 0.9,
                          ...rsvpStyle,
                        }}
                        title={p.note || ""}
                        onClick={(e) => handlePracticeButtonClick(e, p)}
                        disabled={!isPracticeClickable}
                      >
                        <div className="calendarPracticeInfo">
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                            <span className={`calendarPracticeTypeChip type-${normalizePracticeType(p.type)}`}>
                              {p.cancelled ? "Cancelled" : typeLabel}
                            </span>
                            <span className="calendarPracticeStatusIcon" aria-hidden="true">{icon}</span>
                          </div>
                          <span className="calendarPracticeTime" style={p.cancelled ? { textDecoration: "line-through" } : undefined}>
                            {labelTime || "Time?"}
                          </span>
                          <span className="calendarPracticeMeta">
                            {teamName || "All Teams"}
                            {p.squadName && <> · {p.squadName}</>}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        </>
      ) : (
        <div className="card" style={{ padding: 8, marginTop: 6 }}>
          {days.map((dateObj) => {
            const key = fmt(dateObj);
            const isToday = key === todayKey;
            const dayPractices = byDay.get(key) || [];

            return (
              <div
                key={key}
                className="row"
                style={{
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "8px 0",
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <div style={{ width: 110 }}>
                  <div style={{ fontWeight: 600 }}>
                    {dateObj.toLocaleDateString(undefined, { weekday: "short" })}
                  </div>
                  <div className={isToday ? "week-today-date" : "muted"}>
                    {dateObj.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </div>
                </div>

                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                  {dayPractices.length === 0 && (
                    <span className="help" style={{ opacity: 0.6 }}>No practice</span>
                  )}
                  {dayPractices.map((p) => {
                    const labelTime = formatPracticePrimaryLabel(p);
                    const teamName = p.team ? String(p.team).trim() : "";
                    const statusKey = normalizeRsvpStatus(p.rsvpStatus);
                    const rsvpStyle = RSVP_STYLES[statusKey] || RSVP_STYLES.none;
                    const icon = RSVP_ICONS[statusKey] || "?";
                    const typeLabel = formatPracticeTypeLabel(p.type);
                    return (
                      <button
                        key={`${p.id}-${p.date}`}
                        style={{
                          ...rsvpStyle,
                          border: "1px solid rgba(0, 0, 0, 0.12)",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "12px",
                          cursor: isPracticeClickable ? "pointer" : "default",
                          opacity: isPracticeClickable ? 1 : 0.9,
                          fontSize: "13px",
                          textAlign: "left",
                          width: "100%",
                          transition: "transform 0.1s ease, box-shadow 0.1s ease",
                        }}
                        title={p.note || ""}
                        onClick={(e) => handlePracticeButtonClick(e, p)}
                        disabled={!isPracticeClickable}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span className={`calendarPracticeTypeChip type-${normalizePracticeType(p.type)}`}>
                              {typeLabel}
                            </span>
                            <span className="calendarPracticeStatusIcon" aria-hidden="true">{icon}</span>
                          </div>
                          <span style={{ fontWeight: 700, fontSize: "14px" }}>{labelTime || "Time?"}</span>
                          <span style={{ fontSize: "12px", color: "var(--muted)" }}>
                            {teamName || "All Teams"}
                            {p.squadName && <> · {p.squadName}</>}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                  {allowCalendarEdits && (
                    <button
                      className="btn ghost small"
                      style={{ minWidth: "32px", padding: "6px", alignSelf: "flex-start" }}
                      onClick={() => openCreatePractice(key)}
                      title="Add practice"
                    >
                      +
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {allowCalendarEdits && creating && (
        <PracticeModal
          isOpen={Boolean(creating)}
          onClose={() => setCreating(null)}
          practice={{
            id: null,
            date: createForm.date || creating,
            endDate: createForm.endDate || "",
            startTime: createForm.startTime || "",
            endTime: createForm.endTime || "",
            state: createForm.state,
            type: normalizePracticeType(createForm.type),
            team: createForm.team || "",
            squadIds: (createForm.squadIds || []).map(Number).filter(Boolean),
            squad: (createForm.squadIds || []).length > 0
              ? (squads || []).filter((sq) => (createForm.squadIds || []).map(String).includes(String(sq.id))).map((sq) => sq.name).join(", ")
              : "",
            note: createForm.note || "",
            name: createForm.name || "",
            abbrevName: createForm.abbrevName || "",
            location: createForm.location || "",
            responseFields: Array.isArray(createForm.responseFields) ? createForm.responseFields : [],
          }}
          allowEdit
          form={createForm}
          onChangeForm={setCreateForm}
          teams={teams}
          squads={squads}
          onSave={createPractice}
          loadingDetails={false}
          practiceDetails={null}
          coach={coach}
          canEditWorkouts={canEditPractices}
          showRsvpButtons={false}
          startInEditMode
          hideEditToggle
          modalTitle={
            createForm.date
              ? `Create Practice - ${new Date(`${createForm.date}T00:00:00`).toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}`
              : "Create Practice"
          }
        />
      )}

      {allowCalendarEdits && editing && (
        <PracticeModal
          isOpen={Boolean(editing)}
          onClose={() => setEditing(null)}
          practice={editing}
          allowEdit={allowCalendarEdits}
          canEditWorkouts={canEditPractices}
          form={form}
          onChangeForm={setForm}
          teams={teams}
          squads={squads}
          onSave={saveEdit}
          onDelete={deletePractice}
          onAddNote={(practiceId) => setNotePracticeId(practiceId)}
          onCancelPractice={(practiceId) => { setCancelling(practiceId); setCancelNote(""); }}
          loadingDetails={loadingDetails}
          practiceDetails={practiceDetails}
          coach={coach}
          onUpdateOwnAttendance={updateOwnAttendance}
          updatingCoachAttendance={updatingCoachAttendance}
          showRsvpButtons
          rsvpStatus={coach?.id && practiceDetails?.coaches ? practiceDetails.coaches.find((c) => c.userId === coach.id)?.status || null : null}
          onRsvp={(status) => updateOwnAttendance(editing.id, status)}
          enableResponseFields={false}
        />
      )}

      {notePracticeId && (
        <PracticeNoteModal
          practiceId={notePracticeId}
          onClose={() => setNotePracticeId(null)}
        />
      )}

      {cancelling && (
        <div
          className="modalOverlay"
          onClick={() => { if (!cancelSubmitting) setCancelling(null); }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(20, 24, 33, 0.5)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
            padding: 16,
          }}
        >
          <div
            className="modalCard"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 440,
              background: "var(--bg-elevated)",
              color: "var(--text)",
              borderRadius: 18,
              boxShadow: "var(--shadow-elevated)",
              border: "1px solid var(--card-border)",
            }}
          >
            <div style={{ padding: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 36,
                      height: 36,
                      borderRadius: 12,
                      background: "var(--danger-bg)",
                      color: "var(--danger)",
                      flexShrink: 0,
                    }}
                  >
                    <FaExclamationTriangle size={16} />
                  </span>
                  <h3 className="h2" style={{ margin: 0 }}>Cancel Practice</h3>
                </div>
                <button
                  aria-label="Close"
                  onClick={() => { if (!cancelSubmitting) setCancelling(null); }}
                  disabled={cancelSubmitting}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    background: "rgba(20,35,59,0.07)",
                    border: "none",
                    borderRadius: "50%",
                    cursor: cancelSubmitting ? "default" : "pointer",
                    color: "var(--muted)",
                    flexShrink: 0,
                  }}
                >
                  <FaTimes size={14} />
                </button>
              </div>
              <p className="help" style={{ marginBottom: 12 }}>
                This will notify athletes and parents by email and post to Slack. A reason is required.
              </p>
              <textarea
                className="input"
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
                placeholder="Reason for cancellation..."
                rows={3}
                style={{ width: "100%", resize: "vertical", marginBottom: 16 }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button className="btn ghost" onClick={() => setCancelling(null)} disabled={cancelSubmitting}>
                  Back
                </button>
                <button
                  className="btn danger"
                  onClick={submitCancelPractice}
                  disabled={cancelSubmitting || !cancelNote.trim()}
                >
                  {cancelSubmitting ? "Cancelling…" : "Confirm Cancellation"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
